import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useLanguage } from '../contexts/LanguageContext';
import { ExclamationTriangleIcon, EyeIcon, EyeSlashIcon, LockClosedIcon, GlobeAltIcon, InformationCircleIcon, DocumentTextIcon, CalendarIcon, BuildingOffice2Icon, UserIcon } from '@heroicons/react/24/outline';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { Quotation } from '../types/quotation';
import { format } from 'date-fns';

// hCaptcha site key
const HCAPTCHA_SITE_KEY = '43125db8-c85e-45fc-96ba-5f04c20c0e0c';

// Ellenőrizzük, hogy fejlesztői módban vagyunk-e
const isDevelopment = process.env.NODE_ENV === 'development';

const SharedQuotationView = () => {
  const { quotationId } = useParams<{ quotationId: string }>();
  const [searchParams] = useSearchParams();
  const shareId = searchParams.get('share');
  const { t, language, setLanguage } = useLanguage();
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const captchaRef = useRef<HCaptcha>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaError, setCaptchaError] = useState<string | null>(null);
  const [showDebugInfo, setShowDebugInfo] = useState(false);

  const [quotationData, setQuotationData] = useState<Quotation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string | null>(null);

  // Nyelvváltás kezelése
  const toggleLanguage = () => {
    setLanguage(language === 'hu' ? 'en' : 'hu');
    setLanguageMenuOpen(false);
  };

  // hCaptcha token változásakor nullázzuk a hibaüzenetet
  useEffect(() => {
    if (captchaToken) {
      setCaptchaError(null);
    }
  }, [captchaToken]);

  useEffect(() => {
    const validateShare = async () => {
      setLoading(true);
      setError(null);
      setDebugInfo(null);

      try {
        // Ellenőrizzük, hogy van-e megfelelő azonosító
        if (!quotationId) {
          setError(t('shared.invalidLinkMissingParams'));
          setDebugInfo(`quotationId: ${quotationId}, shareId: ${shareId}`);
          setLoading(false);
          return;
        }

        // Cloud Function meghívása jelszó nélkül, ellenőrizzük, hogy kell-e jelszó
        const validateQuotationShare = httpsCallable(functions, 'validateQuotationShare');
        const result = await validateQuotationShare({
          quotationId,
          shareId
        });

        console.log("Cloud Function válasza:", result);

        const data = result.data as any;

        if (data.needsPassword) {
          // Jelszóra van szükség
          setNeedsPassword(true);
          setLoading(false);
        } else {
          // Nincs szükség jelszóra, megkaptuk az árajánlat adatait
          setQuotationData(data.quotation);
          setLoading(false);
        }
      } catch (err: any) {
        console.error('Hiba a megosztott árajánlat elérése során:', err);
        setError(err.message || t('shared.errorLoadingDocument'));

        // Részletesebb hiba információ
        if (err.code) {
          setDebugInfo(`${t('debug.errorCode')}: ${err.code}, ${t('debug.details')}: ${err.details || t('debug.noDetails')}`);
        } else {
          setDebugInfo(`${t('debug.error')}: ${JSON.stringify(err)}`);
        }

        setLoading(false);
      }
    };

    validateShare();
  }, [quotationId, shareId, t]);

  // hCaptcha callback
  const onCaptchaVerify = (token: string) => {
    console.log('hCaptcha token:', token);
    setCaptchaToken(token);
  };

  const onCaptchaError = (err: any) => {
    console.error('hCaptcha hiba:', err);
    setCaptchaError(t('shared.captchaError'));
  };

  const onCaptchaExpire = () => {
    console.log('hCaptcha lejárt');
    setCaptchaToken(null);
  };

  // Jelszó ellenőrzése és árajánlat lekérése
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!captchaToken) {
      setCaptchaError(t('shared.captchaRequired'));
      return;
    }

    setLoading(true);
    setPasswordError(null);

    try {
      const validateQuotationShare = httpsCallable(functions, 'validateQuotationShare');
      const result = await validateQuotationShare({
        quotationId,
        shareId,
        password,
        captchaToken
      });

      const data = result.data as any;

      if (data.success) {
        setQuotationData(data.quotation);
        setNeedsPassword(false);
      } else {
        setPasswordError(t('shared.invalidPassword'));
        if (captchaRef.current) {
          captchaRef.current.resetCaptcha();
          setCaptchaToken(null);
        }
      }
    } catch (err: any) {
      console.error('Hiba a jelszavas ellenőrzés során:', err);
      setPasswordError(err.message || t('shared.errorValidatingPassword'));

      // Részletesebb hiba információ
      if (err.code) {
        setDebugInfo(`${t('debug.errorCode')}: ${err.code}, ${t('debug.details')}: ${err.details || t('debug.noDetails')}`);
      } else {
        setDebugInfo(`${t('debug.error')}: ${JSON.stringify(err)}`);
      }

      if (captchaRef.current) {
        captchaRef.current.resetCaptcha();
        setCaptchaToken(null);
      }
    } finally {
      setLoading(false);
    }
  };

  // Nyelv váltó komponens
  const LanguageSelector = () => (
    <div className="relative">
      <button
        onClick={() => setLanguageMenuOpen(!languageMenuOpen)}
        className="flex items-center bg-gray-700 hover:bg-gray-600 rounded-md px-3 py-2 text-sm text-white"
      >
        <GlobeAltIcon className="h-4 w-4 mr-2" />
        <span>{language === 'hu' ? 'Magyar' : 'English'}</span>
      </button>

      {languageMenuOpen && (
        <div
          className="absolute right-0 mt-2 w-40 bg-gray-800 shadow-lg rounded-md overflow-hidden z-50"
          onClick={() => setLanguageMenuOpen(false)}
        >
          <button
            onClick={toggleLanguage}
            className="w-full text-left px-4 py-2 text-sm text-white hover:bg-gray-700"
          >
            {language === 'hu' ? 'English' : 'Magyar'}
          </button>
        </div>
      )}
    </div>
  );

  // Betöltési képernyő
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col">
        <div className="p-4 flex justify-end">
          <LanguageSelector />
        </div>
        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-500"></div>
          <p className="mt-4 text-gray-300">{t('common.loading') || 'Betöltés...'}</p>
        </div>
      </div>
    );
  }

  // Hibaüzenet megjelenítése
  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col">
        <div className="p-4 flex justify-end">
          <LanguageSelector />
        </div>
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="bg-gray-800 rounded-lg p-8 shadow-lg max-w-md w-full">
            <div className="flex justify-center mb-6">
              <div className="bg-red-900/40 p-3 rounded-full">
                <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
              </div>
            </div>

            <h1 className="text-xl font-bold text-white text-center mb-4">
              {t("shared.error")}
            </h1>

            <p className="text-gray-400 text-center mb-6">
              {error}
            </p>

            {isDevelopment && showDebugInfo && debugInfo && (
              <div className="mt-4 p-3 bg-gray-900 rounded text-xs text-gray-400 overflow-auto max-h-32">
                {debugInfo}
              </div>
            )}

            {isDevelopment && (
              <button
                type="button"
                onClick={() => setShowDebugInfo(!showDebugInfo)}
                className="mt-4 text-xs text-gray-500 flex items-center mx-auto"
              >
                <InformationCircleIcon className="h-3 w-3 mr-1" />
                {showDebugInfo ? "Debug info elrejtése" : "Debug info mutatása"}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Jelszóbekérő űrlap, ha szükséges
  if (needsPassword) {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col">
        <div className="p-4 flex justify-end">
          <LanguageSelector />
        </div>
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="bg-gray-800 rounded-lg p-8 shadow-lg max-w-md w-full">
            <div className="flex justify-center mb-6">
              <div className="bg-primary-900/40 p-3 rounded-full">
                <LockClosedIcon className="h-8 w-8 text-primary-400" />
              </div>
            </div>

            <h1 className="text-xl font-bold text-white text-center mb-2">
              {t("shared.passwordProtected")}
            </h1>

            <p className="text-gray-400 text-center mb-6">
              {t("shared.enterPassword")}
            </p>

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
                  {t("shared.password")}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1 block w-full p-2.5 bg-gray-700 border border-gray-600 rounded-md text-white focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {passwordError && (
                  <p className="mt-2 text-sm text-red-400">{passwordError}</p>
                )}
              </div>

              {/* hCaptcha komponens */}
              <div className="my-4">
                <div className="flex justify-center">
                  <HCaptcha
                    sitekey={HCAPTCHA_SITE_KEY}
                    onVerify={onCaptchaVerify}
                    onError={onCaptchaError}
                    onExpire={onCaptchaExpire}
                    ref={captchaRef}
                    theme="light"
                    size="normal"
                    languageOverride={language}
                  />
                </div>
                {captchaError && (
                  <p className="mt-2 text-sm text-red-400 text-center">{captchaError}</p>
                )}
              </div>

              {isDevelopment && showDebugInfo && debugInfo && (
                <div className="mt-2 p-2 bg-gray-100 rounded text-xs text-gray-600 overflow-auto max-h-24">
                  {debugInfo}
                </div>
              )}

              <button
                type="submit"
                className="w-full px-4 py-2.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors flex items-center justify-center"
                disabled={loading}
              >
                {loading ? (
                  <><span className="animate-spin mr-2">⟳</span> {t("common.loading")}</>
                ) : (
                  t("shared.viewDocument")
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Árajánlat adatok ellenőrzése
  if (!quotationData) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <div className="p-4 flex justify-end">
          <LanguageSelector />
        </div>
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="bg-white rounded-lg p-8 shadow-lg max-w-md w-full">
            <div className="flex justify-center mb-6">
              <div className="bg-red-100 p-3 rounded-full">
                <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
              </div>
            </div>

            <h1 className="text-xl font-bold text-gray-800 text-center mb-4">
              {t("quotations.error.notFound")}
            </h1>

            <p className="text-gray-600 text-center mb-6">
              {t("quotations.error.notFoundMessage")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Kiszámoljuk az árajánlat teljes összegét
  const calculateTotal = () => {
    return quotationData.items.reduce((sum, item) => {
      const price = item.price || item.unitPrice || 0;
      const itemTotal = item.totalWithTax || (price * item.quantity * (1 + item.taxRate / 100));
      return sum + itemTotal;
    }, 0);
  };

  // Árajánlat megtekintése
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Fejléc */}
      <header className="bg-white shadow-md p-4">
        <div className="container mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-800">{quotationData.number}</h1>
            <div className="flex items-center space-x-2">
              <p className="text-gray-600 text-sm">{t(`quotations.status.${quotationData.status}`)}</p>
              {quotationData.shareSettings?.viewCount !== undefined && (
                <div className="flex items-center text-gray-600 text-xs ml-3">
                  <EyeIcon className="h-3.5 w-3.5 mr-1" />
                  <span>{t("quotations.viewCount", { count: quotationData.shareSettings.viewCount })}</span>
                </div>
              )}
            </div>
          </div>
          <LanguageSelector />
        </div>
      </header>

      {/* Árajánlat tartalma */}
      <div className="container mx-auto py-6 px-4">
        <div className="space-y-6">
          {/* Alapadatok */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              {t('quotations.form.basicDetails.title')}
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <span className="text-xs text-gray-500">{t('quotations.fields.organization')}</span>
                <p className="text-sm text-gray-800">{quotationData.organizationName || '-'}</p>
              </div>

              <div>
                <span className="text-xs text-gray-400">{t('quotations.fields.customer')}</span>
                <p className="text-sm text-white">{quotationData.customerName || '-'}</p>
              </div>

              <div>
                <span className="text-xs text-gray-400">{t('quotations.fields.status')}</span>
                <p className="text-sm text-white">{t(`quotations.status.${quotationData.status}`)}</p>
              </div>

              <div>
                <span className="text-xs text-gray-400">{t('quotations.fields.validUntil')}</span>
                <p className="text-sm text-white">
                  {quotationData.validUntil ? format(new Date(quotationData.validUntil), 'yyyy-MM-dd') : '-'}
                </p>
              </div>
            </div>
          </div>

          {/* Cég adatok */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              {t('quotations.form.sections.companyDetails.title')}
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <span className="text-xs text-gray-500">{t('quotations.form.sections.companyDetails.quotationNumber')}</span>
                <p className="text-sm text-gray-800">{quotationData.quotationNumber || '-'}</p>
              </div>

              <div>
                <span className="text-xs text-gray-500">{t('quotations.form.sections.companyDetails.companyName')}</span>
                <p className="text-sm text-gray-800">{quotationData.companyName || '-'}</p>
              </div>

              <div>
                <span className="text-xs text-gray-500">{t('quotations.form.sections.companyDetails.companyAddress')}</span>
                <p className="text-sm text-gray-800">{quotationData.companyAddress || '-'}</p>
              </div>

              <div>
                <span className="text-xs text-gray-500">{t('quotations.form.sections.companyDetails.taxNumber')}</span>
                <p className="text-sm text-gray-800">{quotationData.taxNumber || '-'}</p>
              </div>

              <div>
                <span className="text-xs text-gray-500">{t('quotations.form.sections.companyDetails.registrationNumber')}</span>
                <p className="text-sm text-gray-800">{quotationData.registrationNumber || '-'}</p>
              </div>
            </div>
          </div>

          {/* Kapcsolattartási és árazási adatok */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              {t('quotations.form.sections.contactAndPricing.title')}
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <span className="text-xs text-gray-500">{t('quotations.form.sections.contactAndPricing.contactName')}</span>
                <p className="text-sm text-gray-800">{quotationData.contactName || '-'}</p>
              </div>

              <div>
                <span className="text-xs text-gray-500">{t('quotations.form.sections.contactAndPricing.date')}</span>
                <p className="text-sm text-gray-800">
                  {quotationData.date ? format(new Date(quotationData.date), 'yyyy-MM-dd') : '-'}
                </p>
              </div>

              <div>
                <span className="text-xs text-gray-500">{t('quotations.form.sections.contactAndPricing.paymentDue')}</span>
                <p className="text-sm text-gray-800">
                  {quotationData.paymentDue ? `${quotationData.paymentDue}` : '-'}
                </p>
              </div>

              <div>
                <span className="text-xs text-gray-500">{t('quotations.form.sections.contactAndPricing.price')}</span>
                <p className="text-sm text-gray-800">{quotationData.price || '-'}</p>
              </div>

              <div>
                <span className="text-xs text-gray-500">{t('quotations.form.sections.contactAndPricing.priceUnit')}</span>
                <p className="text-sm text-gray-800">{quotationData.priceUnit || '-'}</p>
              </div>
            </div>
          </div>

          {/* Fogyasztási és szerződési adatok */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              {t('quotations.form.sections.consumptionAndContract.title')}
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <span className="text-xs text-gray-500">{t('quotations.form.sections.consumptionAndContract.consumption')}</span>
                <p className="text-sm text-gray-800">{quotationData.consumption || '-'}</p>
              </div>

              <div>
                <span className="text-xs text-gray-500">{t('quotations.form.sections.consumptionAndContract.contractStart')}</span>
                <p className="text-sm text-gray-800">
                  {quotationData.contractStart ? format(new Date(quotationData.contractStart), 'yyyy-MM-dd') : '-'}
                </p>
              </div>

              <div>
                <span className="text-xs text-gray-500">{t('quotations.form.sections.consumptionAndContract.contractEnd')}</span>
                <p className="text-sm text-gray-800">
                  {quotationData.contractEnd ? format(new Date(quotationData.contractEnd), 'yyyy-MM-dd') : '-'}
                </p>
              </div>
            </div>
          </div>

          {/* Teljes áramköltség számítás */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Becsült áramköltség
            </h3>
            <div className="space-y-2">
              {quotationData.price && quotationData.consumption && (
                <div className="flex flex-col">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-500">Ajánlati ár:</span>
                    <span className="text-sm text-gray-800">{quotationData.price} {quotationData.priceUnit || '-'}</span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-500">Fogyasztás:</span>
                    <span className="text-sm text-gray-800">{quotationData.consumption} kWh</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-gray-200 mt-2">
                    <span className="text-sm font-medium text-gray-700">Becsült összköltség:</span>
                    <span className="text-sm font-bold text-primary-600">
                      {(() => {
                        try {
                          const price = parseFloat(quotationData.price);
                          const consumption = parseFloat(quotationData.consumption);
                          let totalCost = 0;
                          let currency = 'HUF';
                          
                          // Ellenőrzöm az árformátumot és számolom a teljes költséget
                          if (quotationData.priceUnit?.includes('EUR/MWh')) {
                            // EUR/MWh formátum - a fogyasztást MWh-ba kell konvertálni
                            totalCost = price * (consumption / 1000);
                            currency = 'EUR';
                          } else if (quotationData.priceUnit?.includes('HUF/kWh')) {
                            // HUF/kWh formátum - közvetlen szorzás
                            totalCost = price * consumption;
                            currency = 'HUF';
                          }
                          
                          return new Intl.NumberFormat('hu-HU', {
                            style: 'currency',
                            currency: currency,
                            maximumFractionDigits: 2
                          }).format(totalCost);
                        } catch (e) {
                          return "Nem számítható";
                        }
                      })()}
                    </span>
                  </div>
                </div>
              )}
              {(!quotationData.price || !quotationData.consumption) && (
                <p className="text-sm text-gray-500">Az áramköltség kiszámításához hiányoznak adatok.</p>
              )}
            </div>
          </div>

          {/* KÁT szöveg */}
          {quotationData.kat?.text && (
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                {t('quotations.form.sections.kat.title')}
              </h3>
              <p className="text-sm text-gray-800">{quotationData.kat.text}</p>
            </div>
          )}

          {/* POD adatok */}
          {quotationData.pod && (
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                {t('quotations.form.sections.pod.title')}
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <span className="text-xs text-gray-500">{t('quotations.form.sections.pod.podId')}</span>
                  <p className="text-sm text-gray-800">{quotationData.pod?.id || '-'}</p>
                </div>

                <div>
                  <span className="text-xs text-gray-500">{t('quotations.form.sections.pod.podAddress')}</span>
                  <p className="text-sm text-gray-800">{quotationData.pod?.address || '-'}</p>
                </div>

                <div>
                  <span className="text-xs text-gray-500">{t('quotations.form.sections.pod.measurementType')}</span>
                  <p className="text-sm text-gray-800">{quotationData.pod?.measurementType || '-'}</p>
                </div>

                <div>
                  <span className="text-xs text-gray-500">{t('quotations.form.sections.pod.power')}</span>
                  <p className="text-sm text-gray-800">{quotationData.pod?.power || '-'}</p>
                </div>

                <div>
                  <span className="text-xs text-gray-500">{t('quotations.form.sections.pod.profileNumber')}</span>
                  <p className="text-sm text-gray-800">{quotationData.pod?.profileNumber || '-'}</p>
                </div>

                <div>
                  <span className="text-xs text-gray-500">{t('quotations.form.sections.pod.annualConsumption')}</span>
                  <p className="text-sm text-gray-800">{quotationData.pod?.annualConsumption || '-'}</p>
                </div>
              </div>
            </div>
          )}

          {/* További POD-ok */}
          {quotationData.additionalPods && quotationData.additionalPods.length > 0 && (
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                {t('quotations.form.sections.additionalPods.title')}
              </h3>

              <div className="space-y-4">
                {quotationData.additionalPods.map((pod, index) => (
                  <div key={index} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <span className="text-xs text-gray-500">{t('quotations.form.sections.additionalPods.podId', { index: index + 2 })}</span>
                      <p className="text-sm text-gray-800">{pod.id || '-'}</p>
                    </div>

                    <div>
                      <span className="text-xs text-gray-500">{t('quotations.form.sections.additionalPods.podAddress', { index: index + 2 })}</span>
                      <p className="text-sm text-gray-800">{pod.address || '-'}</p>
                    </div>

                    <div>
                      <span className="text-xs text-gray-500">{t('quotations.form.sections.additionalPods.measurementType', { index: index + 2 })}</span>
                      <p className="text-sm text-gray-800">{pod.measurementType || '-'}</p>
                    </div>

                    <div>
                      <span className="text-xs text-gray-500">{t('quotations.form.sections.additionalPods.power', { index: index + 2 })}</span>
                      <p className="text-sm text-gray-800">{pod.power || '-'}</p>
                    </div>

                    <div>
                      <span className="text-xs text-gray-500">{t('quotations.form.sections.additionalPods.profileNumber', { index: index + 2 })}</span>
                      <p className="text-sm text-gray-800">{pod.profileNumber || '-'}</p>
                    </div>

                    <div>
                      <span className="text-xs text-gray-500">{t('quotations.form.sections.additionalPods.annualConsumption', { index: index + 2 })}</span>
                      <p className="text-sm text-gray-800">{pod.annualConsumption || '-'}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Dokumentumok */}
          {quotationData.documents && quotationData.documents.length > 0 && (
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                {t('quotations.form.sections.documents.title')}
              </h3>

              <div className="space-y-2">
                {quotationData.documents.map((document, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 bg-gray-700/50 rounded-lg"
                  >
                    <div className="flex items-center">
                      <DocumentTextIcon className="w-5 h-5 text-primary-400 mr-2" />
                      <span className="text-sm text-white">{document.name}</span>
                    </div>
                    <a
                      href={document.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary-400 hover:text-primary-300"
                    >
                      {t('common.download')}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Megjegyzések */}
          {quotationData.notes && (
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                {t('quotations.form.notes.title')}
              </h3>
              <p className="text-sm text-gray-800">{quotationData.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SharedQuotationView; 